import { formatDateOrDateTime } from '@cascade/core/utils';
import { useRowSelection } from '@cascade/react/hooks';
import { useConversations } from '@cascade/react/conversations';
import { useI18n } from '@cascade/react/i18n';
import {
  Button,
  Checkbox,
  DataView,
  emptyConfig,
  Table,
  Text,
  useToast,
} from '@cascade/ui-library';
import type {
  TDataViewConfig,
  TDataViewFilterDef,
  TDataViewSortDef,
  TTableColumn,
} from '@cascade/ui-library';
import { useMemo, useState } from 'react';
import {
  buildConversationRow,
  buildConversationsQuery,
  cycleSortsFromColumn,
  DATE_FIELD_ID,
  getColumnDirectionFromSorts,
  TITLE_FIELD_ID,
} from './DataControls.helpers.ts';
import classes from './DataControls.module.scss';
import type { TConversationRow } from './DataControls.types.ts';
import { useConversationsExport } from './use-conversations-export/index.ts';
import { ConfirmDialog } from '#components/confirm-dialog/index.ts';
import { Page } from '#components/page/index.ts';

const SELECT_COLUMN_WIDTH = 56;
const STARTED_COLUMN_WIDTH = 200;

export const DataControls = () => {
  const { locale, translate, translateError } = useI18n();
  const { toast } = useToast();
  const {
    isLoadingConversations,
    conversationsError,
    isDeletingConversations,
    queryConversations,
    deleteConversations,
  } = useConversations();

  const { isExporting, exportConversations } = useConversationsExport();
  const [config, setConfig] = useState<TDataViewConfig>(emptyConfig);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const query = useMemo(() => buildConversationsQuery(config), [config]);

  const visibleConversations = useMemo(
    () => queryConversations(query),
    [queryConversations, query],
  );

  const visibleRows = useMemo(
    () => visibleConversations.map(buildConversationRow),
    [visibleConversations],
  );

  const visibleIds = visibleRows.map((row) => row.id);

  const {
    selectedIds,
    hasSelection,
    isPartlySelected,
    isRowSelected,
    setRowSelected,
    toggleAll,
    clearSelection,
  } = useRowSelection(visibleIds);

  const selectedConversations = visibleConversations.filter((conversation) =>
    isRowSelected(conversation.conversation_id),
  );

  const selectedCount = selectedIds.length;
  const isBusy = isExporting || isDeletingConversations;

  const placeholder = conversationsError
    ? translateError(conversationsError)
    : translate('dataControls.none');

  const filterDefs: TDataViewFilterDef[] = [
    {
      id: DATE_FIELD_ID,
      label: translate('dataControls.started'),
      type: 'date',
      mode: 'range',
    },
  ];

  const sortDefs: TDataViewSortDef[] = [
    { id: DATE_FIELD_ID, label: translate('dataControls.started') },
    { id: TITLE_FIELD_ID, label: translate('dataControls.conversation') },
  ];

  const sortByColumn = (fieldId: string) => {
    setConfig((previous) => ({
      ...previous,
      sorts: cycleSortsFromColumn(previous.sorts, fieldId),
    }));
  };

  const columns: TTableColumn<TConversationRow>[] = [
    {
      id: 'select',
      label: translate('dataControls.selectAll'),
      width: SELECT_COLUMN_WIDTH,
      align: 'center',
      header: (
        <Checkbox
          label={translate('dataControls.selectAll')}
          labelDisplay='hidden'
          value={hasSelection}
          indeterminate={isPartlySelected}
          disabled={visibleRows.length === 0 || isBusy}
          onChange={toggleAll}
        />
      ),
      renderer: (row) => (
        <Checkbox
          label={translate('dataControls.select', { title: row.title })}
          labelDisplay='hidden'
          value={isRowSelected(row.id)}
          disabled={isBusy}
          onChange={(isSelected) => setRowSelected(row.id, isSelected)}
        />
      ),
    },
    {
      id: TITLE_FIELD_ID,
      label: translate('dataControls.conversation'),
      width: 'fill',
      wrap: true,
      renderer: (row) => <Text type='body-2' content={row.title} compact />,
      sorting: {
        direction: getColumnDirectionFromSorts(config.sorts, TITLE_FIELD_ID),
        onChange: () => sortByColumn(TITLE_FIELD_ID),
      },
    },
    {
      id: DATE_FIELD_ID,
      label: translate('dataControls.started'),
      width: STARTED_COLUMN_WIDTH,
      sorting: {
        direction: getColumnDirectionFromSorts(config.sorts, DATE_FIELD_ID),
        onChange: () => sortByColumn(DATE_FIELD_ID),
      },
      renderer: (row) => (
        <Text
          type='body-2'
          content={formatDateOrDateTime(row.date, { locale })}
          compact
        />
      ),
    },
  ];

  const downloadSelected = () => {
    exportConversations(selectedConversations)
      .then(() =>
        toast({
          title: translate('dataControls.downloaded'),
          variant: 'success',
        }),
      )
      .catch((error: unknown) =>
        toast({
          title: translate('dataControls.downloadFailed'),
          description: translateError(error),
          variant: 'danger',
        }),
      );
  };

  const openDeleteDialog = () => setIsDeleteDialogOpen(true);
  const closeDeleteDialog = () => setIsDeleteDialogOpen(false);

  const confirmDelete = () => {
    deleteConversations(selectedIds)
      .then(() => {
        clearSelection();
        toast({ title: translate('dataControls.deleted'), variant: 'success' });
      })
      .catch((error: unknown) =>
        toast({
          title: translate('dataControls.deleteFailed'),
          description: translateError(error),
          variant: 'danger',
        }),
      )
      .finally(closeDeleteDialog);
  };

  return (
    <Page iconType='user-shield' title={translate('dataControls.title')}>
      <div className={classes.toolbar}>
        <DataView
          aria-label={translate('dataControls.view')}
          className={classes.view}
          config={config}
          searchable
          searchPlaceholder={translate('dataControls.search')}
          filters={filterDefs}
          sorts={sortDefs}
          disabled={isBusy}
          onChange={setConfig}
        />
        <div className={classes.actions}>
          {hasSelection ? (
            <Text
              type='body-2'
              color='text-60'
              content={translate('dataControls.selected', {
                count: selectedCount,
              })}
              compact
            />
          ) : null}
          <Button
            variant='secondary'
            size='sm'
            iconType='download'
            label={translate('dataControls.download')}
            disabled={!hasSelection || isBusy}
            onClick={downloadSelected}
          />
          <Button
            variant='danger'
            size='sm'
            iconType='trash'
            label={translate('dataControls.delete')}
            disabled={!hasSelection || isBusy}
            onClick={openDeleteDialog}
          />
        </div>
      </div>
      <Table
        aria-label={translate('dataControls.table')}
        columns={columns}
        data={visibleRows}
        isLoading={isLoadingConversations}
        placeholder={placeholder}
        className={classes.table}
      />
      {isDeleteDialogOpen ? (
        <ConfirmDialog
          title={translate('dataControls.delete.title')}
          description={translate('dataControls.delete.description', {
            count: selectedCount,
          })}
          confirmLabel={translate('dataControls.delete.confirm')}
          cancelLabel={translate('dataControls.delete.cancel')}
          closeLabel={translate('dataControls.delete.close')}
          isDestructive
          isPending={isDeletingConversations}
          onConfirm={confirmDelete}
          onClose={closeDeleteDialog}
        />
      ) : null}
    </Page>
  );
};
